/**
 * Shared helpers for routing a recipient into the Build Itinerary setup wizard
 * for a distributed schedule copy (Phase 6C entry points).
 *
 * A distributed copy carries `isDistributedCopy: true` and, until the recipient
 * finishes the wizard, `needsSetup: true`. The wizard is launched by navigating
 * to `/schedule/build-itinerary?eventId=<copyId>` — it starts the recipient at
 * step 2 (Match Teams) and clears `needsSetup` on finish.
 */

import type { CalendarEventResponse } from "../../../../types/api";

/** The route that launches the setup wizard for a given distributed copy. */
export function itinerarySetupUrl(eventId: string): string {
  return `/schedule/build-itinerary?eventId=${encodeURIComponent(eventId)}`;
}

/** What RosterPilot staff carry into the wizard when they build an itinerary a program asked for. */
export interface ItineraryRequestLaunch {
  requestId: string;
  /** The organization that asked, so the share step at the end knows who to send it to. */
  organizationId: string;
  organizationName: string;
  tournamentName: string;
  /** The program's schedule link, when they gave one — it seeds the import in step 1. */
  scheduleUrl?: string | null;
}

/**
 * The wizard URL for building an itinerary a program requested.
 *
 * Deliberately CREATE mode (no `eventId`): the tournament does not exist yet, which is the whole
 * reason there is a request. The extra params are context, not state — they prefill step 1 and tell
 * the final share step who asked, and the wizard works without any of them.
 *
 * The organization name travels in the URL rather than being fetched. The queue row already has it
 * and the wizard has no admin-scoped endpoint to look one up with — a coach's own org list would not
 * contain another program.
 */
export function itineraryBuildForRequestUrl(launch: ItineraryRequestLaunch): string {
  const params = new URLSearchParams({
    requestId: launch.requestId,
    requestOrgId: launch.organizationId,
    requestOrgName: launch.organizationName,
    name: launch.tournamentName,
  });
  if (launch.scheduleUrl) {
    params.set("scheduleUrl", launch.scheduleUrl);
  }
  return `/schedule/build-itinerary?${params.toString()}`;
}

/** True when an event is a distributed copy the recipient still needs to set up. */
export function eventNeedsSetup(event: CalendarEventResponse): boolean {
  return event.isDistributedCopy && event.needsSetup;
}

/**
 * sessionStorage key recording that we auto-launched setup for a given copy this
 * session. Guards the one-time auto-launch: after the recipient cancels out
 * (which returns to /schedule) and reopens the event, we do NOT auto-launch
 * again — the persistent button is enough. Scoped to the session so a fresh tab
 * still gets the first-open auto-launch.
 */
export function autolaunchKey(eventId: string): string {
  return `itinerary-setup-autolaunched:${eventId}`;
}

/**
 * True once this copy's setup has been auto-launched this session. Reads are
 * wrapped so a disabled/throwing sessionStorage (private mode) degrades to
 * "not yet launched" rather than crashing — worst case is one extra launch, not
 * a broken view.
 */
export function hasAutolaunched(eventId: string): boolean {
  try {
    return sessionStorage.getItem(autolaunchKey(eventId)) != null;
  } catch {
    return false;
  }
}

/** Record that this copy's setup was auto-launched this session. */
export function markAutolaunched(eventId: string): void {
  try {
    sessionStorage.setItem(autolaunchKey(eventId), "1");
  } catch {
    /* non-fatal: guard degrades to allowing another launch */
  }
}

/**
 * Reads the build-from-request context back off the wizard's URL, or null when the wizard was
 * opened any other way.
 *
 * All-or-nothing on the three ids: a half-populated launch would render a share step that either
 * has no recipient or cannot close the request, which is worse than not offering one. The schedule
 * link is genuinely optional — programs are not required to supply one.
 */
export function readRequestLaunch(params: URLSearchParams): ItineraryRequestLaunch | null {
  const requestId = params.get("requestId");
  const organizationId = params.get("requestOrgId");
  const organizationName = params.get("requestOrgName");
  const tournamentName = params.get("name");

  if (!requestId || !organizationId || !organizationName || !tournamentName) {
    return null;
  }

  return {
    requestId,
    organizationId,
    organizationName,
    tournamentName,
    scheduleUrl: params.get("scheduleUrl"),
  };
}
